/**
 * Centralized registry for event-related React Query keys.
 *
 * Mirrors the pattern in `@lib/cache/spaces` and `@lib/cache/questions`
 * (issue #653: "No raw strings should be used for cache keys"). Adding
 * a new key family is a one-line change here, and consumers get
 * type-checked factories instead of free-form arrays that drift across
 * files.
 *
 * Coverage today: every event-related cache key consumed by production
 * code paths. Producers live in `api-client.ts`; invalidators live in
 * `CreateEvent.tsx`, `EventDetails.tsx`, `Events.tsx`,
 * `AdminSettings.tsx`, `EventImageUploadSimple.tsx`,
 * `SimpleEventCard.tsx`, `useCallbackRedirect.ts`,
 * `useNativePostAuth.ts`, `useRealtimeInvalidation.ts`,
 * `usePublicEvents.ts`, and `useInfiniteEvents.ts`.
 *
 * Out of scope here: `spaceEventsKey` (community-scoped event lists)
 * lives in `@lib/cache/spaces` because it is keyed by `spaceId`, not by
 * any of the event-list parameter shapes below.
 *
 * Naming convention: the `Base` suffix marks prefix-only factories used
 * by invalidators that want to nuke an entire key family across all
 * parameter shapes; the un-suffixed factories produce the exact key a
 * specific consumer reads or writes.
 */

import type { EventMatchCandidatesRequest } from '@models/api';

/**
 * `pov` ("point of view") narrows event-list queries to the user's
 * upcoming events or past events. Exported so callers cannot widen the
 * type at a call site: a typo like `'upcomming'` would type-check under
 * `string` and silently never refetch.
 */
export type EventsPov = 'upcoming' | 'past';

/**
 * Filter views that are BOTH a UI tab and a valid backend `GET /events?view=` param.
 * The shared base for the two view unions below.
 */
export type EventFeedView = 'all' | 'saved' | 'going';

/**
 * Event-list UI tab: the base feed views plus the UI-only `my-calendars` (served by its own
 * endpoint, `GET /events/my-calendars` — never sent as a `view` query param). Used for tab
 * state and cache-key namespacing.
 */
export type EventListView = EventFeedView | 'my-calendars';

/**
 * Backend `view` query param for `GET /events` and `GET /spaces/{id}/events`: the base feed
 * views plus the backend-only `checked-in`, and notably NO `my-calendars`. Wire calls type
 * their `view` as this so a UI-only tab can never reach the backend (which would 422).
 */
export type EventViewParam = EventFeedView | 'checked-in';

// ── Lists: base prefixes (use for prefix invalidation) ─────────────────────

/**
 * Base prefix for ALL general event-list caches. Used after create,
 * update, or delete to refresh every events-flavored feed in one call.
 * Prefix-matches `eventsListKey()`, `eventsTotalsKey()`,
 * `eventsPublicKey()`, and `eventsInfiniteKey()`.
 */
export const eventsBaseKey = () => ['events'] as const;

/**
 * Base prefix for featured-event caches across all parameter shapes.
 * Prefix-matches `featuredEventsListKey()`.
 */
export const featuredEventsBaseKey = () => ['featured-events'] as const;

/**
 * Base prefix for the current user's checked-in events caches across
 * all `includeMeetverse` values. Prefix-matches `myCheckedInEventsKey()`.
 */
export const myCheckedInEventsBaseKey = () => ['myCheckedInEvents'] as const;

/**
 * Base prefix for resolve-event caches across all slugs/IDs. Used by
 * realtime SSE handlers that don't know which specific slug to invalidate
 * but need to nuke every cached single-event-by-slug entry.
 */
export const resolveEventBaseKey = () => ['resolve-event'] as const;

/**
 * Base prefix for event-participation caches across all events/users.
 * Reserved for the cross-event "log out / switch accounts" case where
 * every cached participation row must be dropped at once. No current
 * caller needs that breadth; `eventParticipationByEventKey()` (scoped
 * to one specific event) covers every live invalidation site.
 */
export const eventParticipationBaseKey = () => ['eventParticipation'] as const;

// ── Lists: parameterized producers (used by useQuery hooks) ────────────────

type EventsListParams =
  | {
      page?: number;
      per_page?: number;
      user_id?: string;
      pov?: EventsPov;
      search?: string;
    }
  | undefined;

/**
 * Paginated events list keyed by the full params object. The params
 * object is passed through untouched; React Query uses structural
 * equality, so two calls with deep-equal params hit the same cache
 * entry.
 *
 * Producer: `useReadEventsApiV1EventsGet` in `api-client.ts`. That hook
 * has no live application consumer today (`useInfiniteEvents` is the
 * only events-list path actually rendered, and it bypasses the orval
 * hook to call `readEventsApiV1EventsGet` directly). Factory kept for
 * symmetry with the producer and so a future consumer has the correct
 * shape pre-typed.
 */
export const eventsListKey = (params?: EventsListParams) => ['events', params] as const;

type EventsTotalsParams = { pov?: EventsPov; user_id?: string } | undefined;

/**
 * Aggregate totals (upcoming / live / past / total counts) for events
 * filtered by params.
 *
 * Producer: `useReadEventsTotalsApiV1EventsTotalsGet` in `api-client.ts`.
 * That hook has no live application consumer today. Factory kept for
 * symmetry with the producer.
 */
export const eventsTotalsKey = (params?: EventsTotalsParams) =>
  ['events', 'totals', params] as const;

/**
 * Public events list shown to unauthenticated guests. Fixed key with no
 * params; the producer always asks for `{limit: 100, include_past: false}`.
 * Mirrors `usePublicEvents`.
 */
export const eventsPublicKey = () => ['events', 'public'] as const;

/**
 * Infinite-scroll events query. Normalizes `pov || 'all'` and
 * `search || ''` here so callers (producer and future invalidators)
 * automatically agree on the shape, mirroring `useInfiniteEvents`'s
 * inline normalization at its `queryKey` site.
 *
 * `view` is typed `EventFeedView` (all / saved / going), NOT `EventListView`: the general
 * `/events` feed never caches under the UI-only `'my-calendars'` tab (that has its own
 * `myCalendarsInfiniteKey`), so the narrower type keeps an impossible cache slot unrepresentable.
 * Defaults to `'all'` so existing 2-arg call sites keep their slot. The segment stays under the
 * `['events', ...]` prefix, so `eventsBaseKey()` invalidation still covers every view.
 */
export const eventsInfiniteKey = (
  pov: EventsPov | undefined,
  search: string | undefined,
  view: EventFeedView = 'all',
) => ['events', pov ?? 'all', search ?? '', view] as const;

/** 2-element prefix for invalidating every My Calendars search at once (e.g. on join/leave). */
export const myCalendarsBaseKey = () => ['events', 'my-calendars'] as const;

/**
 * Combined "My Calendars" feed (events across the user's joined spaces). Kept under the
 * `['events', ...]` prefix so `eventsBaseKey()` invalidation refreshes it for free.
 * The single-space "tap a space" state reuses `spaceEventsKey` instead.
 */
export const myCalendarsInfiniteKey = (search?: string) =>
  ['events', 'my-calendars', search ?? ''] as const;

/**
 * Live-layer rail (design spec §7): a single plain `useQuery` over the same
 * `GET /events/my-calendars` upcoming feed, partitioned client-side into the
 * soon/live rail, live-space set, and Home pill. Distinct cache slot from
 * `myCalendarsInfiniteKey` (own page size, own `staleTime`) but nested under the
 * `myCalendarsBaseKey()` prefix on purpose: the rail's content is a function of
 * the user's space MEMBERSHIPS, and every membership invalidator (join/leave/
 * create/invitation flows + the SSE self-membership handler) sweeps that base —
 * so a join lights the rail up without any of those five call sites knowing it
 * exists. `eventsBaseKey()` (the `event.updated` SSE handler) still reaches it
 * for free via the shared `['events', ...]` root. The third segment is an
 * OBJECT, not a string, so it can never collide with `myCalendarsInfiniteKey`'s
 * search segment (raw user input — someone can literally search "live-rail",
 * and the two entries' data shapes differ: `EventRead[]` vs `InfiniteData`).
 * House precedent for object segments: `eventsListKey` / `eventsTotalsKey`.
 * No params: one rail per user session. Consumed by `useLiveEventsRail`.
 */
export const eventsLiveRailKey = () => ['events', 'my-calendars', { slot: 'live-rail' }] as const;

/**
 * Live-layer recap source (design spec §5.1; recap/pill redesign §2): a small
 * `useQuery` over `GET /events/my-recaps` — the user's GOING events whose
 * `end_date` fell inside the server's recap window (`RECAP_WINDOW_HOURS`, 48h),
 * ordered most-recently-ended first, `preview_users` enriched. Unlike the
 * rail's `pov=upcoming` feed — which drops ended events (`end_date >= now`) so
 * its `recapEvent` is only transiently populated — this is a RELIABLE source
 * for the recap cards: the consumer (`useLiveRecaps`) keeps only the
 * `ended-recap` phase client-side (`recapWindowMs` aligns with the server
 * window). Distinct cache slot from `myCalendarsInfiniteKey` and
 * `eventsLiveRailKey` (own page size + `staleTime`), kept under the shared
 * `['events', ...]` prefix so `eventsBaseKey()` invalidation and the existing
 * `event.updated` SSE handler keep it honest for free. No params: one recap
 * feed per user session.
 */
export const eventsLiveRecapKey = () => ['events', 'live-recap'] as const;

/**
 * Meetverse live pill row, call 1 (recap/pill redesign §1): a small `useQuery`
 * over the PUBLIC `GET /events/live` with no filter param — live + starting-soon
 * events in the server's live-first order, guests included (the only
 * unauthenticated live-layer cache). "Live + soon" is the entry's exact meaning
 * again as of the split-fetch ruling: the upcoming tail is a SEPARATE request
 * under `eventsUpcomingTailKey()`, so nothing parameterizes this one and a
 * parameterless factory cannot misdescribe what the entry holds. One row per
 * session — no params (page 1, the hook's own compact page size), the
 * `eventsLiveRecapKey` idiom. Kept under the shared `['events', ...]` prefix so
 * `eventsBaseKey()` invalidation and `applyEventUpdate`'s list sweep (the entry
 * holds `EventRead[]`) reach it for free. No collision with `eventsInfiniteKey`:
 * `'live-now'` is not a member of `EventsPov`, its second segment. Consumed
 * by `useMeetverseLiveEvents`.
 */
export const eventsLiveNowKey = () => ['events', 'live-now'] as const;

/**
 * Meetverse live pill row, call 2: the same PUBLIC `GET /events/live` asked
 * with `only_upcoming: true` — ONLY the band beyond the soon window,
 * soonest-first, horizon-bounded server-side.
 *
 * A separate KEY because it is a separate request with a separate meaning.
 * Folding the upcoming tail into `eventsLiveNowKey()` (which promises "live +
 * soon") would hand the next plain `getLiveEvents()` consumer — a live-only
 * widget, a prefetch, a test seed — whichever payload mounted first: far-future
 * rows rendered as currently-live pills, or the meetverse row silently losing
 * its tail, with nothing erroring at any layer (Dexter's review of PR #1035).
 * One meaning, one call shape, one key; both stay parameterless because both
 * requests are fixed. Kept under the shared `['events', ...]` prefix for the
 * same free `eventsBaseKey()` / `applyEventUpdate` reach as its sibling, and
 * `'upcoming-tail'` is not a member of `EventsPov`, so it cannot collide with
 * `eventsInfiniteKey`'s second segment either. Consumed by
 * `useMeetverseLiveEvents`; swept alongside `eventsLiveNowKey()` by
 * `useRealtimeInvalidation`'s event-clock handlers.
 */
export const eventsUpcomingTailKey = () => ['events', 'upcoming-tail'] as const;

/**
 * Base prefix for the events-tab just-ended family across every `view` shape.
 * Prefix-matches `eventsTabJustEndedKey(view)` regardless of which filter tab
 * built it. Used by `useRealtimeInvalidation`'s event-clock handlers
 * (`event.went_live` / `event.ended`): a server-side phase transition flips
 * which events belong to the recap strip, and the handler doesn't know which
 * `view` tabs are mounted, so it sweeps the whole family via this base rather
 * than enumerating views client-side. Prefix-match invalidation only refetches
 * MOUNTED just-ended queries, so the blast radius stays bounded to what's on
 * screen. Mirrors `eventsFeedCountsBaseKey()`'s base-plus-parameterized pattern.
 */
export const eventsTabJustEndedBaseKey = () => ['events', 'just-ended'] as const;

/**
 * Events-tab recap strip (T-6, recap/pill redesign §3): a small `useQuery`
 * over `GET /events?pov=past&per_page=10` with the SAME `view` as the active
 * feed, client-filtered to events whose `end_date` is within
 * `eventsTabRecapWindowMs` (48h). Keyed by `view` so a Saved/Going-filtered
 * tab gets its own slot (the response really differs per view). Distinct from
 * `eventsInfiniteKey('past', …)` (own page size + `staleTime`; that one holds
 * `InfiniteData`, this one `EventRead[]`) — no collision is possible because
 * `'just-ended'` is not a member of `EventsPov`, the infinite key's second
 * segment (the segment name predates the 48h widening; the CACHE key is
 * deliberately stable across the rename of the config lever). Kept under the
 * shared `['events', ...]` prefix so `eventsBaseKey()` invalidation and
 * `applyEventUpdate`'s list sweep reach it for free. Built on
 * `eventsTabJustEndedBaseKey()` so producer and invalidators share one factory
 * for the family prefix. Consumed by `useEventsTabJustEnded`.
 */
export const eventsTabJustEndedKey = (view: EventFeedView) =>
  [...eventsTabJustEndedBaseKey(), view] as const;

/** Hard cap from the backend contract: `GET /events/feed-counts` accepts ≤ 30 `event_ids` per call. */
export const EVENT_FEED_COUNTS_MAX_IDS = 30;

/**
 * Canonical id-list shape for a feed-counts batch: deduped, sorted, capped at
 * the endpoint's 30-id limit. Both the cache KEY (`eventsFeedCountsKey`) and
 * the REQUEST (`useEventFeedCounts`'s queryFn) derive from this one function,
 * so any two call sites passing the same id set — in any order, with any
 * duplication — share a single cache entry AND ask the backend for exactly
 * the ids that entry is keyed by.
 *
 * The over-cap case (>30 ids means 30+ events simultaneously soon/live on ONE
 * surface — conference-day scale, never expected in practice): sort-then-slice
 * keeps the drop deterministic across permuted inputs — capping by caller
 * order instead would fragment the cache, since two hosts passing the same
 * set in different orders must share one entry. Ids beyond the cap are absent
 * from the key AND the request, and the consumers' per-id pending gate
 * (`isCountPending`) makes them render nothing — indistinguishable from an id
 * the viewer can't see, never a skeleton that can't resolve. The cap itself
 * is the backend wire contract (≤ 30 per call, backend #623); if >30
 * concurrently-live ids ever becomes real, the follow-up is chunking the
 * REQUESTS (≤ 30 each) behind this same normalized-key contract, not lifting
 * the cap here.
 */
export const normalizeEventFeedCountIds = (eventIds: readonly string[]): string[] =>
  [...new Set(eventIds)].sort().slice(0, EVENT_FEED_COUNTS_MAX_IDS);

/**
 * Base prefix for the feed-counts family across every id-list batch shape.
 * Prefix-matches `eventsFeedCountsKey(eventIds)` regardless of which
 * normalized id list a given batch resolved to. Used by `useRealtimeInvalidation`'s
 * question.created / space.question_created handlers and the create-question
 * mutation's local echo (#966): a new post can move an event's feed-counts
 * pill through paths the client can't cheaply enumerate (see those call
 * sites' own comments for the rationale), so both invalidate this whole
 * family rather than computing affected event ids client-side. Prefix-match
 * invalidation only refetches MOUNTED counts queries, so the blast radius
 * stays bounded to whatever's actually on screen.
 */
export const eventsFeedCountsBaseKey = () => ['events', 'feed-counts'] as const;

/**
 * Universal feed-aligned posts counts for a batch of events (T-8 ruling,
 * revised 2026-07-12: the count is the event's universal activity count —
 * the same number for every viewer, guests included — served by the public
 * `GET /events/feed-counts`; only WHICH ids appear still honors per-viewer
 * event visibility). Keyed by the NORMALIZED id list so call order and
 * duplicates can never fragment the cache — and by NOTHING viewer-shaped:
 * guest and authed sessions deliberately share entries, since the value is
 * identity-independent. Lives under the shared `['events', ...]` root so
 * `eventsBaseKey()` invalidation (create/update/delete flows AND the native
 * post-auth sweep in `useNativePostAuth`) reaches it for free — but it is
 * NOT a list cache (the entry holds `{ counts: Record<id, number> }`, not an
 * event collection), so it is excluded from `applyEventUpdate`'s list sweep
 * via `NON_LIST_EVENT_SUBKEYS`. Built on `eventsFeedCountsBaseKey()` so
 * producer and invalidators share one factory for the family prefix.
 * Consumed by `useEventFeedCounts`.
 */
export const eventsFeedCountsKey = (eventIds: readonly string[]) =>
  [...eventsFeedCountsBaseKey(), normalizeEventFeedCountIds(eventIds)] as const;

type FeaturedEventsListParams = { skip?: number; limit?: number } | undefined;

/**
 * Paginated featured-events list. Mirrors
 * `useReadFeaturedEventsApiV1EventsFeaturedGet`.
 *
 * Note: `Events.tsx` currently overrides the producer's queryKey to
 * `featuredEventsBaseKey()` (length-1), collapsing the cache to a single
 * slot since `{skip: 0, limit: 10}` is the only call shape. This
 * parameterized factory is reserved for any future caller that wants
 * per-params caching.
 */
export const featuredEventsListKey = (params?: FeaturedEventsListParams) =>
  ['featured-events', params] as const;

/**
 * My-checked-in-events list keyed by the `includeMeetverse` boolean.
 * Mirrors `useGetMyCheckedInEventsApiV1EventsMyCheckedInGet`. Pass
 * `undefined` to match the producer's no-arg invocation.
 */
export const myCheckedInEventsKey = (includeMeetverse: boolean | undefined) =>
  ['myCheckedInEvents', includeMeetverse] as const;

// ── Single event detail ────────────────────────────────────────────────────

/** Root of ALL id-keyed event-detail caches (['event', id] and subkeys like
 *  attendees). Distinct from the plural ['events'] list root; family-wide
 *  operations that must reach mounted detail screens and attendee rosters
 *  (e.g. user.updated identity refresh) need BOTH roots. */
export const eventDetailBaseKey = () => ['event'] as const;

/**
 * Single event detail by UUID. Lives at `['event', id]` (singular
 * root), which is a distinct cache root from the `['events', ...]`
 * list family, so `invalidateQueries({ queryKey: eventsBaseKey() })`
 * does NOT reach detail caches. Invalidators that mutate a single
 * event must call this factory in addition to the list base. Mirrors
 * `useReadEventApiV1EventsEventIdGet`.
 */
export const eventKey = (eventId: string) => [...eventDetailBaseKey(), eventId] as const;

/**
 * Root of every Going-toggle mutation key (`eventGoingMutationKey`). A mutation filter on it
 * matches the toggles of every Event, so it is the filter to subscribe with when the Event is not
 * known yet: react-query evaluates a `useMutationState` / `useIsMutating` filter when the hook
 * mounts and when the mutation cache notifies, never because the filter itself changed.
 */
export const eventGoingMutationBaseKey = () => ['event-going'] as const;

/**
 * Mutation key for the Going toggle on one Event (useToggleGoingEvent): the root, then the Event's
 * id. TECH-456's posting gate watches the root and picks its Event's id out of the pending keys:
 * while a toggle for the Event is in flight anywhere but the gate's own prompt (the Event page's
 * pill, whose (+) opens the composer while its POST is still pending; an un-Going; an earlier
 * mount of the gate) the gate shows its loader rather than prompt over it. When that toggle ends
 * the gate re-reads the Event and the joined-Spaces list itself, and only then decides: what is
 * cached by then can still be a snapshot from before the toggle committed.
 */
export const eventGoingMutationKey = (eventId: string) =>
  [...eventGoingMutationBaseKey(), eventId] as const;

/**
 * Search-less prefix of `eventAttendeesKey` — invalidates an event's Going roster across
 * EVERY attendee-search variant in one call (mirrors `myCalendarsBaseKey`'s relationship to
 * `myCalendarsInfiniteKey`). Used by the SSE participation handler: its in-place `going_count`
 * patch skips the `eventKey(id)` invalidation that the local `useToggleGoingEvent` relies on to
 * sweep the roster by prefix, so the roster (un-patchable from a scalar payload) is refreshed
 * through this prefix instead. Stays future-proof once Slice 2's attendee search lands.
 */
export const eventAttendeesBaseKey = (eventId: string) =>
  [...eventDetailBaseKey(), eventId, 'attendees'] as const;

/**
 * Going roster for a specific event. Nested under the `['event', id]` detail root so
 * `eventKey(id)` invalidation sweeps it, and keyed by the search term (`''` until
 * Slice 2 adds attendee search). Producer: `useEventAttendees`.
 */
export const eventAttendeesKey = (eventId: string, search?: string) =>
  [...eventAttendeesBaseKey(eventId), search ?? ''] as const;

/**
 * Viewer-scoped helpable-at-live-event read for one event (Track 2 Phase A):
 * the section cards AND the live pill's count from a single fetch. Nested under
 * the `['event', id]` detail root (like `eventKey`/`eventAttendeesKey`), so a
 * single-event invalidation - notably `useToggleGoingEvent`'s `eventKey(id)`
 * sweep when the going set changes - refreshes helpable membership for free
 * (author-in-going-set is one of the endpoint's criteria). Keyed by the same
 * slug-or-UUID the caller resolves the live event with. Producer:
 * `useEventHelpable`. Invalidate on helpable dismiss (optimistic) and event
 * phase-flip to ended.
 */
export const eventHelpableKey = (eventId: string) => ['event', eventId, 'helpable'] as const;

/**
 * The viewer's live join holds on one event (TECH-663): the PIN Spaces whose host has not been
 * told about the viewer's request yet (`GET /events/{id}/join-holds`). Read by the Event page to
 * reopen the PIN sheet (`useEventJoinHoldsReopen`). Nested under the `['event', id]` detail root,
 * so the Going settle's `eventKey(id)` sweep (`invalidateGoingEventQueries`, which a PIN join also
 * runs) refreshes it: a Going, an un-Going and a PIN join all change it. `useEventPinJoin` also
 * drops a Space from it the moment the sheet answers for it.
 */
export const eventJoinHoldsKey = (eventId: string) =>
  [...eventDetailBaseKey(), eventId, 'join-holds'] as const;

// ── Single event resolution by slug-or-UUID ────────────────────────────────

/**
 * Resolves an event by either slug or UUID. CreateEvent's update flow
 * invalidates this so subsequent reads pick up renamed/edited events.
 * Mirrors `useResolveEventBySlug`.
 */
export const resolveEventKey = (slugOrId: string) => ['resolve-event', slugOrId] as const;

// ── Event participation ────────────────────────────────────────────────────

/**
 * Prefix for all participation rows of a specific event (across every
 * user). Used by `EventDetails.tsx`'s checkin flow to nuke cached
 * participation rows when the user's status changes.
 */
export const eventParticipationByEventKey = (eventId: string) =>
  ['eventParticipation', eventId] as const;

/**
 * User's participation status for a specific event. Mirrors
 * `useGetUserParticipationStatusApiV1EventsEventIdParticipationGet`.
 */
export const eventParticipationKey = (eventId: string, userId: string) =>
  ['eventParticipation', eventId, userId] as const;

/**
 * The create-time duplicate check (`POST /events/match-candidates`), keyed by the whole request
 * body: two forms asking the same thing share one answer. A standalone root, deliberately NOT
 * under `['events', ...]`: its data is `EventMatch[]`, not an event list, so `applyEventUpdate`'s
 * list sweep and the `eventsBaseKey()` invalidations must never reach it.
 * Produced by `useDuplicateMatches`.
 */
export const eventMatchCandidatesKey = (body: EventMatchCandidatesRequest) =>
  ['eventMatchCandidates', body] as const;

// ── Predicates ───────────────────────────────────────────────────────────────

/**
 * Prefixes that identify event *list* caches (general feed, featured, my-calendars, and
 * space-scoped).
 *
 * `'spaceEvents'` is a foreign root owned by `@lib/cache/spaces` (`spaceEventsKey`), not this
 * module -- included here anyway so `applyEventUpdate`'s single predicate sweep
 * (`src/lib/cache/events/operations.ts`) also reaches the Space Events tab's cached lists
 * (design spec `2026-07-09-p3-going-liveness-design.md` §6: "event list roots ... spaceEvents").
 * Mirrors `QUESTION_LIST_PREFIXES`'s cross-cutting reach in `@lib/cache/questions`, except
 * questions' space-scoped lists happen to nest under the shared `'questions'` root while events'
 * space-scoped lists deliberately don't (see `spaceEventsKey`'s own docstring above).
 *
 * `'myCheckedInEvents'` is deliberately NOT included: its consumers (CreateQuestion,
 * CrosspostModal, WeMetSpacesDrawer) are event-picker/selector UI, not a going-count display
 * surface, so it is out of scope for the going-liveness patch.
 *
 * When you add a new event-list family, append its first element string here. `applyEventUpdate`
 * picks it up automatically via `isEventListKey`.
 */
export const EVENT_LIST_PREFIXES = ['events', 'featured-events', 'spaceEvents'] as const;

/**
 * Second-segment values under `['events', ...]` that are NOT lists.
 *
 * `eventsTotalsKey` returns `{ upcoming, live, past, total }` and
 * `eventsFeedCountsKey` returns `{ counts: Record<id, number> }` — neither is
 * an event collection; including them in `isEventListKey` would needlessly
 * sweep those caches during `applyEventUpdate`'s list walk.
 */
const NON_LIST_EVENT_SUBKEYS = ['totals', 'feed-counts'] as const;

/**
 * Does this query key belong to an event list cache?
 *
 * Used by `applyEventUpdate`'s `setQueriesData` predicate so every list family registered in
 * `EVENT_LIST_PREFIXES` is covered without hand-matching branches. False for detail
 * (`eventKey`), roster (`eventAttendeesKey`), slug-resolver (`resolveEventKey`), and unrelated
 * (`eventParticipation*`) roots -- those are patched by `applyEventUpdate`'s own dedicated
 * branches or left alone.
 */
export const isEventListKey = (key: readonly unknown[]): boolean => {
  const prefixIsEventKey = (EVENT_LIST_PREFIXES as readonly string[]).includes(String(key[0]));

  if (!prefixIsEventKey) return false;

  return !(NON_LIST_EVENT_SUBKEYS as readonly string[]).includes(String(key[1]));
};

export const isResolveEventKey = (key: readonly unknown[]): boolean =>
  key[0] === 'resolve-event' && typeof key[1] === 'string';
